// /app/ajustes/mapeo-cuentas — overrides del cliente sobre el árbol del
// plan de cuentas. Si no hay override, el export usa los códigos default del
// árbol HATO. La página lista las cuentas del árbol con su default y el
// override (si existe) y permite editarlos.
//
// Sprint 1 (2026-05-16): refactor del modelo plano de 25 SemanticAccount al
// árbol. La página itera el árbol del cliente, no una lista hardcoded.

import { useCurrentAccount } from "@/features/accounts/hooks/useCurrentAccount";
import { Check, Sparkles } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  type ChartMappingRow,
  type SeedMapping,
  useApplySeed,
  useMappingsList,
  useSeedMappings,
  useUpsertMapping,
} from "../api/journalApi";

type RowDraft = {
  accountChartId: string;
  semanticKey: string | null;
  defaultCode: string;
  defaultName: string;
  code: string;
  name: string;
  saved: boolean;
  isSystem: boolean;
};

function buildDrafts(mappings: ChartMappingRow[], seed: SeedMapping[] | undefined): RowDraft[] {
  const seedByKey = new Map(seed?.map((s) => [s.semanticKey, s]) ?? []);
  return mappings.map((m) => {
    const sugg = m.semanticKey ? seedByKey.get(m.semanticKey) : undefined;
    const hasOverride = m.overrideCode !== null;
    return {
      accountChartId: m.accountChartId,
      semanticKey: m.semanticKey,
      defaultCode: m.defaultCode,
      defaultName: m.defaultName,
      // Si tiene override → mostrar el override. Si no → ofrecer el seed CR
      // como sugerencia editable. Si tampoco hay seed → vacío.
      code: m.overrideCode ?? sugg?.code ?? "",
      name: m.overrideName ?? sugg?.name ?? "",
      saved: hasOverride,
      isSystem: m.isSystem,
    };
  });
}

export function OnboardingCuentasPage() {
  const { currentAccount, currentMembership } = useCurrentAccount();
  const accountId = currentAccount?.id ?? "";
  const role = currentMembership?.role;
  const isReadOnly = role === "viewer";
  const canApplySeed = role === "owner" || role === "admin";

  const mappingsQuery = useMappingsList(accountId);
  const seedQuery = useSeedMappings(accountId, canApplySeed);
  const upsert = useUpsertMapping(accountId);
  const applySeed = useApplySeed(accountId);

  const [drafts, setDrafts] = useState<RowDraft[]>([]);

  useEffect(() => {
    if (!mappingsQuery.data) return;
    setDrafts(buildDrafts(mappingsQuery.data.mappings, seedQuery.data?.mappings));
  }, [mappingsQuery.data, seedQuery.data]);

  const totalSystem = useMemo(() => drafts.filter((d) => d.isSystem).length, [drafts]);
  const totalConfigured = useMemo(() => drafts.filter((d) => d.saved).length, [drafts]);

  function updateDraft(chartId: string, patch: Partial<RowDraft>) {
    setDrafts((prev) =>
      prev.map((d) => (d.accountChartId === chartId ? { ...d, ...patch, saved: false } : d)),
    );
  }

  async function saveRow(d: RowDraft) {
    if (!d.code.trim() || !d.name.trim()) {
      toast.error("Código y nombre son obligatorios");
      return;
    }
    try {
      await upsert.mutateAsync({
        accountChartId: d.accountChartId,
        code: d.code.trim(),
        name: d.name.trim(),
      });
      setDrafts((prev) =>
        prev.map((x) => (x.accountChartId === d.accountChartId ? { ...x, saved: true } : x)),
      );
      toast.success("Guardado");
    } catch {
      toast.error("No se pudo guardar");
    }
  }

  async function applyTemplate() {
    if (!seedQuery.data) return;
    try {
      const result = await applySeed.mutateAsync({
        mappings: seedQuery.data.mappings,
        force: false,
      });
      toast.success(
        `Plantilla aplicada (${result.created} creados, ${result.updated} actualizados)`,
      );
    } catch (e) {
      const apiErr = e as { status?: number; code?: string };
      if (apiErr.status === 409 && apiErr.code === "MAPPINGS_ALREADY_EXIST") {
        if (
          window.confirm(
            "Ya tenés mappings configurados. ¿Sobrescribir con la plantilla CR? Esto reemplaza tus códigos por los sugeridos.",
          )
        ) {
          await applySeed.mutateAsync({
            mappings: seedQuery.data.mappings,
            force: true,
          });
          toast.success("Plantilla aplicada con sobrescritura");
        }
      } else {
        toast.error("No se pudo aplicar la plantilla");
      }
    }
  }

  if (mappingsQuery.isLoading) {
    return (
      <>
        <title>Mapeo de cuentas · HATO</title>
        <header className="mb-4">
          <h1 className="font-medium text-3xl text-app-ink-1 tracking-tight">Mapeo de cuentas</h1>
        </header>
        <div className="space-y-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              // biome-ignore lint/suspicious/noArrayIndexKey: skeleton
              key={i}
              className="h-14 animate-pulse rounded-2xl border border-app-border bg-app-surface-2"
            />
          ))}
        </div>
      </>
    );
  }

  // B68: estado vacío = nadie tradujo ningún código todavía. Se explica qué
  // pasa si no se hace nada y se ofrece la plantilla como salida rápida.
  const isEmpty = totalConfigured === 0;

  return (
    <>
      <title>Mapeo de cuentas · HATO</title>
      <header className="mb-2 flex flex-col gap-2 sm:flex-row sm:items-baseline sm:justify-between">
        <div>
          <h1 className="font-medium text-3xl text-app-ink-1 tracking-tight">Mapeo de cuentas</h1>
          <p className="mt-1 text-app-ink-3 text-sm">
            Acá traducís los códigos de cuentas de HATO a los códigos que usa tu contador.
          </p>
        </div>
        {canApplySeed && seedQuery.data && !isEmpty && (
          <button
            type="button"
            onClick={() => void applyTemplate()}
            disabled={applySeed.isPending}
            className="inline-flex items-center gap-2 self-start rounded-2xl border border-app-border bg-app-surface px-4 py-2 font-medium text-[13px] text-app-ink-1 transition-colors hover:bg-app-surface-2 disabled:opacity-50"
          >
            <Sparkles className="h-4 w-4" aria-hidden />
            Aplicar plantilla sugerida (Costa Rica)
          </button>
        )}
      </header>

      {/* B68 (AI-D25): intro con ejemplo concreto — un contador profesional
          no captó el concepto desde la UI anterior. El listón: alguien sin
          contexto lo entiende en una leída. */}
      <div className="mb-4 rounded-2xl border border-app-border bg-app-surface-2 px-4 py-3 text-[14px] text-app-ink-2">
        <p>
          HATO usa el código <span className="font-medium font-mono">1.3.5</span> para{" "}
          <span className="font-medium">Vacas en producción</span>. Si tu contador usa otro código
          en su sistema — digamos <span className="font-medium font-mono">040401</span> — escribilo
          acá, y el Excel que le entregás saldrá con{" "}
          <span className="font-medium font-mono">040401</span>. Así tu contador ve sus propios
          códigos, sin retrabajo.
        </p>
      </div>

      {isReadOnly && (
        <div className="mb-4 rounded-2xl border border-app-border bg-app-surface px-4 py-3 text-app-ink-2 text-sm">
          Estás viendo este mapeo en modo solo lectura. Solo el dueño o un administrador pueden
          editarlo.
        </div>
      )}

      {isEmpty && !isReadOnly && (
        <div className="mb-4 rounded-2xl border border-app-border bg-app-surface px-4 py-4">
          <p className="font-medium text-[14px] text-app-ink-1">
            Todavía no tradujiste ningún código.
          </p>
          <p className="mt-1 text-[13px] text-app-ink-2">
            No pasa nada: si no hacés nada, el Excel sale con los códigos HATO (1.1, 1.3.5, 5.1…) y
            funciona igual — solo que tu contador verá códigos que no son los de su sistema. Cuando
            te pase su catálogo de cuentas, cargá sus códigos acá, fila por fila.
          </p>
          {canApplySeed && seedQuery.data && (
            <>
              <p className="mt-2 text-[13px] text-app-ink-2">
                ¿Querés arrancar con algo? La plantilla sugerida trae códigos típicos de
                contabilidad ganadera en Costa Rica — después podés ajustar cualquier fila.
              </p>
              <button
                type="button"
                onClick={() => void applyTemplate()}
                disabled={applySeed.isPending}
                className="mt-3 inline-flex items-center gap-2 rounded-xl bg-app-ink-1 px-4 py-2.5 font-medium text-[14px] text-white transition-colors hover:bg-[#1e211d] disabled:opacity-50"
              >
                <Sparkles className="h-4 w-4" aria-hidden />
                {applySeed.isPending ? "Aplicando…" : "Aplicar plantilla sugerida (Costa Rica)"}
              </button>
            </>
          )}
        </div>
      )}

      <p className="mb-4 text-app-ink-3 text-xs">
        {totalConfigured} de {totalSystem} cuentas del sistema con código personalizado
      </p>

      <div className="space-y-2">
        {drafts
          .filter((d) => d.isSystem)
          .map((d) => (
            <MappingRow
              key={d.accountChartId}
              draft={d}
              readOnly={isReadOnly}
              onChange={(patch) => updateDraft(d.accountChartId, patch)}
              onSave={() => void saveRow(d)}
              saving={upsert.isPending}
            />
          ))}
      </div>
    </>
  );
}

type MappingRowProps = {
  draft: RowDraft;
  readOnly: boolean;
  onChange: (patch: Partial<RowDraft>) => void;
  onSave: () => void;
  saving: boolean;
};

function MappingRow({ draft, readOnly, onChange, onSave, saving }: MappingRowProps) {
  return (
    <div className="grid grid-cols-1 gap-2 rounded-2xl border border-app-border bg-app-surface p-3 sm:grid-cols-[1fr_140px_1fr_auto] sm:items-center">
      <div className="min-w-0">
        <div className="font-medium text-[14px] text-app-ink-1">{draft.defaultName}</div>
        <div className="text-app-ink-3 text-xs">
          Default HATO: <span className="font-mono">{draft.defaultCode}</span>
        </div>
      </div>
      <input
        type="text"
        value={draft.code}
        onChange={(e) => onChange({ code: e.target.value })}
        disabled={readOnly}
        placeholder={draft.defaultCode}
        aria-label={`Código contable para ${draft.defaultName}`}
        maxLength={50}
        className="rounded-xl border border-app-border bg-app-bg px-3 py-2 text-app-ink-1 text-base disabled:opacity-60"
      />
      <input
        type="text"
        value={draft.name}
        onChange={(e) => onChange({ name: e.target.value })}
        disabled={readOnly}
        placeholder={draft.defaultName}
        aria-label={`Nombre contable para ${draft.defaultName}`}
        maxLength={200}
        className="rounded-xl border border-app-border bg-app-bg px-3 py-2 text-app-ink-1 text-base disabled:opacity-60"
      />
      {!readOnly && (
        <button
          type="button"
          onClick={onSave}
          disabled={saving}
          className="inline-flex min-w-[110px] items-center justify-center gap-1 rounded-xl bg-app-ink-1 px-3 py-2 font-medium text-[13px] text-white transition-colors hover:bg-[#1e211d] disabled:opacity-50"
        >
          {draft.saved ? (
            <>
              <Check className="h-4 w-4" aria-hidden />
              Guardado
            </>
          ) : (
            "Guardar"
          )}
        </button>
      )}
      {readOnly && draft.saved && (
        <span className="inline-flex items-center justify-center gap-1 text-app-ink-3 text-xs">
          <Check className="h-3.5 w-3.5" aria-hidden />
          Configurado
        </span>
      )}
    </div>
  );
}
